import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { canInAnyScope } from '@testflow/shared';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type CatalogColumn, CatalogTable, columnWidth } from '../../components/catalog-table.js';
import { SearchEmptyNotice } from '../../components/search-empty-notice.js';
import { ApiError } from '../../lib/api.js';
import { type GlossaryTerm, catalogApi } from '../../lib/catalog-api.js';
import { useScopedUser } from '../../lib/project-roles.js';
import { matchesQuery } from '../../lib/row-filter.js';

// #52: Der Glossar-Tab war die Referenzimplementierung — jetzt liefert er nur noch seine
// Spalten an CatalogTable. Die Quelle ist eine Auswahl statt eines klickbaren StateChip:
// ein Chip signalisiert im Rest der Anwendung „Anzeige", nicht „Bedienelement" (#48).

type Draft = { term_de: string; term_en: string; source: string; notes: string | null };

// I4 (Review-Finding): Breiten getrennt von den (i18n-behafteten) Spaltendefinitionen exportiert,
// damit catalog-table.test.tsx computeTableMinWidth gegen die ECHTEN Breiten dieses Tabs pruefen
// kann, statt gegen einen synthetischen Spaltensatz. `notes` hat bewusst KEINE eigene Breite
// (undefined) — das ist genau die unbepreiste Spalte, die der bisherige Regressionsschutz-Test
// gar nicht kannte.
export const GLOSSARY_COLUMN_WIDTHS: (string | undefined)[] = ['24%', '24%', '18%', undefined];

export function GlossaryTab({ projectId, q }: { projectId: string; q?: string | undefined }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useScopedUser(projectId);
  // Gleiches Muster wie `canDefects` in app-shell.tsx:109 — heute zeigen die Katalog-Tabs
  // ihre Bedienelemente jedem und der Server antwortet dem Betrachter mit 403.
  const canEdit = user ? canInAnyScope(user, 'catalog.edit') : false;

  const { data: terms, isLoading } = useQuery({
    queryKey: ['glossary', projectId],
    queryFn: () => catalogApi.listGlossary(projectId),
  });

  // #78: Freitextfilter ueber die bereits geladenen Begriffe (kein neuer Endpunkt). matchesQuery
  // (Task 3) buendelt die Regel (getrimmt, kleingeschrieben, kein Fuzzy).
  const trimmedQuery = (q ?? '').trim();
  const searching = trimmedQuery.length > 0;
  const rows = terms ?? [];
  const filteredRows = useMemo(() => {
    if (!searching) return rows;
    return rows.filter((r) => matchesQuery(trimmedQuery, r.term_de, r.term_en, r.notes));
  }, [rows, searching, trimmedQuery]);

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['glossary', projectId] });

  // errorToast: false auf allen drei Mutationen — CatalogTable zeigt Anlegen-/Bearbeiten- und
  // Loesch-Fehler bereits namentlich in der Zeile (Spec Kapitel 2), der globale Toast waere hier
  // die doppelte, generische zweite Meldung (#52-Review Finding 1).
  const createTerm = useMutation({
    mutationFn: (d: Draft) => catalogApi.createGlossary(projectId, d),
    meta: { successToast: 'feedback.created', errorToast: false },
    onSuccess: invalidate,
  });
  const updateTerm = useMutation({
    mutationFn: ({ id, d }: { id: string; d: Draft }) =>
      catalogApi.updateGlossary(projectId, id, d),
    meta: { errorToast: false },
    onSuccess: invalidate,
  });
  const deleteTerm = useMutation({
    mutationFn: (id: string) => catalogApi.deleteGlossary(projectId, id),
    meta: { successToast: 'feedback.deleted', errorToast: false },
    onSuccess: invalidate,
  });

  const sourceOptions = [
    { value: 'sap_standard', label: t('catalog.source.sap_standard') },
    { value: 'kundenspezifisch', label: t('catalog.source.kundenspezifisch') },
  ];

  const columns: CatalogColumn<GlossaryTerm, Draft>[] = [
    {
      key: 'term_de',
      header: t('catalog.fields.termDe'),
      ...columnWidth(GLOSSARY_COLUMN_WIDTHS[0]),
      render: (r) => r.term_de,
      editor: { kind: 'text', get: (d) => d.term_de, set: (d, v) => ({ ...d, term_de: v }) },
    },
    {
      key: 'term_en',
      header: t('catalog.fields.termEn'),
      ...columnWidth(GLOSSARY_COLUMN_WIDTHS[1]),
      render: (r) => r.term_en,
      editor: { kind: 'text', get: (d) => d.term_en, set: (d, v) => ({ ...d, term_en: v }) },
    },
    {
      key: 'source',
      header: t('catalog.fields.source'),
      ...columnWidth(GLOSSARY_COLUMN_WIDTHS[2]),
      render: (r) => t(`catalog.source.${r.source}`),
      editor: {
        // Zweiwertiges Pflichtfeld — nie leer, deshalb ohne allowEmpty (#52-Review Finding 1):
        // die Leeroption entspraeche sonst einem Zustand, den es fachlich nicht gibt.
        kind: 'select',
        get: (d) => d.source,
        // v ist laut CatalogEditor "string | null" (die Leeroption der Komponente), kommt hier
        // aber nie vor, weil allowEmpty fehlt. Die Zusicherung haelt Draft.source als string,
        // ohne einen Fallback-Wert vorzutaeuschen, der nie greift.
        set: (d, v) => ({ ...d, source: v as string }),
        options: sourceOptions,
      },
    },
    {
      key: 'notes',
      header: t('catalog.fields.notes'),
      render: (r) => r.notes ?? '—',
      editor: {
        kind: 'textarea',
        get: (d) => d.notes ?? '',
        set: (d, v) => ({ ...d, notes: v || null }),
      },
    },
  ];

  if (isLoading) return <div className="p-6 text-sm text-text-muted">{t('catalog.loading')}</div>;

  return (
    <div className="h-full overflow-y-auto p-6">
      <h2 className="mb-3 text-sm font-bold text-text-primary">{t('catalog.tabs.glossary')}</h2>
      {searching && filteredRows.length === 0 && (
        <SearchEmptyNotice query={trimmedQuery} className="mb-3" />
      )}
      <CatalogTable
        columns={columns}
        rows={filteredRows}
        rowKey={(r) => r.term_id}
        toDraft={(r) => ({
          term_de: r.term_de,
          term_en: r.term_en,
          source: r.source,
          notes: r.notes,
        })}
        emptyDraft={{ term_de: '', term_en: '', source: 'kundenspezifisch', notes: null }}
        onCreate={(d) => createTerm.mutateAsync(d)}
        onUpdate={(id, d) => updateTerm.mutateAsync({ id, d })}
        onDelete={(id) => deleteTerm.mutateAsync(id)}
        deleteLabel={(r) => r.term_de}
        mapError={(err) =>
          err instanceof ApiError && err.status === 409
            ? { columnKey: 'term_de', message: t('catalog.errors.termExists') }
            : null
        }
        canEdit={canEdit}
        addLabel={t('catalog.actions.addGlossaryTerm')}
      />
    </div>
  );
}
